import type {
  Fare,
  FareSliceDeparture,
  FareSliceOutbound,
  Flights,
  MulticityFlights,
  Prices,
} from "@b2bportal/air-shopping-api";
import type { IFlightListData } from "@b2bportal/core-types";
import type { IFilterState } from "../../../../search/store";
import * as filters from "./processFilters";

export type FilterOptions = {
  allFiltersSelector: IFilterState;
  selectedFaresOptionsArray: string[];

  hasSetStopsOption: boolean;
  hasSetDepartureTimeRange: boolean;
  hasSetArrivalTimeRange: boolean;
  hasSetAirlineFilter: boolean;
  hasSetAirportFilter: boolean;
  hasSetFlightNumberFilter: boolean;
  hasSetMaxPriceFilter: boolean;
  hasSetFareClassFilter: boolean;

  isInChooseReturnStep: boolean;
  flights: Flights | MulticityFlights;
  flightList: IFlightListData<Fare | FareSliceOutbound | FareSliceDeparture>[];
};

export const meetsFilterPredicates =
  (filterOptions: FilterOptions) =>
  (flight: IFlightListData<Fare | FareSliceOutbound | FareSliceDeparture>) => {
    const {
      hasSetFareClassFilter,
      hasSetStopsOption,
      hasSetAirlineFilter,
      hasSetAirportFilter,
      hasSetMaxPriceFilter,
      hasSetArrivalTimeRange,
      hasSetDepartureTimeRange,
      hasSetFlightNumberFilter,
      isInChooseReturnStep,
      flights,
      selectedFaresOptionsArray,
      allFiltersSelector: {
        stopsOption,
        maxPriceFilter,
        outboundDepartureTimeRange,
        outboundArrivalTimeRange,
        returnDepartureTimeRange,
        returnArrivalTimeRange,
        airlineFilter,
        airportFilter,
        flightNumberFilter,
      },
    } = filterOptions;

    let meetsConditions = true;

    const flightSlice = flights.slices[flight.sliceId];

    if (hasSetStopsOption) {
      meetsConditions =
        meetsConditions &&
        filters.performStopOptionFilterV2(flightSlice, stopsOption);
    }

    if (
      (hasSetDepartureTimeRange || hasSetArrivalTimeRange) &&
      !isInChooseReturnStep
    ) {
      meetsConditions =
        meetsConditions &&
        filters.performTimeRangeFilterV2(
          flightSlice,
          outboundDepartureTimeRange,
          outboundArrivalTimeRange
        );
    }

    if (
      (hasSetDepartureTimeRange || hasSetArrivalTimeRange) &&
      isInChooseReturnStep
    ) {
      meetsConditions =
        meetsConditions &&
        filters.performTimeRangeFilterV2(
          flightSlice,
          returnDepartureTimeRange,
          returnArrivalTimeRange
        );
    }

    if (hasSetAirlineFilter) {
      meetsConditions =
        meetsConditions &&
        filters.performAirlineFilterV2(flightSlice, airlineFilter);
    }

    if (hasSetAirportFilter) {
      meetsConditions =
        meetsConditions &&
        filters.performAirportFilterV2(flightSlice, airportFilter);
    }

    if (hasSetFlightNumberFilter) {
      meetsConditions =
        meetsConditions &&
        filters.performFlightNumberFilterV2(flightSlice, flightNumberFilter);
    }

    let faresFilteredByClass: { amount: Prices; rating: number }[] | undefined;

    if (hasSetFareClassFilter) {
      faresFilteredByClass = filters.performFareClassFilterV2(
        flights,
        flight,
        selectedFaresOptionsArray
      );
      meetsConditions = meetsConditions && faresFilteredByClass.length > 0;
    }

    if (hasSetMaxPriceFilter) {
      const flightFaresAmount =
        faresFilteredByClass ??
        flight.fares.flatMap((fare) => {
          if (fare.amount == null) return [];
          return { amount: fare.amount };
        });

      const priceFilterResult = filters.performMaxPriceFilterV2(
        flightFaresAmount,
        maxPriceFilter
      );

      meetsConditions = meetsConditions && priceFilterResult;
    }

    return meetsConditions;
  };
